import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  Table,
  Space,
  message,
  InputNumber,
  DatePicker,
  Checkbox,
  Tooltip,
  Card,
  Row,
  Col,
  Tag,
  Divider,
} from "antd";
import {
  DollarOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { addBulkPaymentApi } from "../../../services/Payment/PaymentApis";
import { getGroupMembersWithBalancesApi } from "../../../services/Group/GroupApis";
import { showApiErrorMessage } from "../../../utils/systemValidation";

const { Option } = Select;

interface IModalProps {
  show: boolean;
  group: any;
  showModal: Function;
  onSuccess?: () => void;
}

interface IMemberPayment {
  memberId: string;
  memberName: string;
  membershipNumber: string;
  premium: number;
  outstanding: number;
  amountToAllocate: number;
  selected: boolean;
}

const BulkPaymentModal: React.FC<IModalProps> = ({
  show,
  group,
  showModal,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [members, setMembers] = useState<IMemberPayment[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [allocatedAmount, setAllocatedAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState<Dayjs>(dayjs());
  const [paymentReference, setPaymentReference] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("Cash");
  const [notes, setNotes] = useState<string>("");

  const handleModal = () => {
    form.resetFields();
    setMembers([]);
    setTotalAmount(0);
    setAllocatedAmount(0);
    setPaymentReference("");
    setNotes("");
    showModal();
  };

  const fetchGroupMembers = async () => {
    setLoadingMembers(true);
    try {
      // Pass the group level to filter members hierarchically
      const groupLevel = group.GroupLevel || group.groupLevel;
      const response = await getGroupMembersWithBalancesApi(group.id, groupLevel);
      
      // Handle different response structures
      let memberList = [];
      
      if (response.data) {
        // Check if response.data is the array directly
        if (Array.isArray(response.data)) {
          memberList = response.data;
        }
        // Check if data is nested in response.data.data
        else if (response.data.data && Array.isArray(response.data.data)) {
          memberList = response.data.data;
        }
        // Check if it's wrapped in a success object
        else if (response.data.success && response.data.data) {
          memberList = Array.isArray(response.data.data) ? response.data.data : [];
        }
      }
      
      if (memberList.length === 0) {
        console.warn("No members found for group:", group.id);
        message.info("No members found for this group. Please add members before processing bulk payments.");
        setMembers([]);
        return;
      }
      
      const memberData = memberList.map((member: any) => ({
        memberId: String(member.memberId || member.id || member.MemberId || ""),
        memberName: member.memberName || member.name || member.Name || member.FullName || "Unknown",
        membershipNumber: member.membershipNumber || member.MembershipNumber || member.memberNumber || "N/A",
        premium: Number(member.premium || member.Premium || 0),
        outstanding: Number(member.outstanding || member.Outstanding || member.outstandingBalance || 0),
        amountToAllocate: 0,
        selected: false,
      }));
      
      setMembers(memberData);
    } catch (error: any) {
      console.error("Error fetching group members:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response,
        data: error.response?.data
      });
      
      showApiErrorMessage(error, "loading group members");
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchGroupMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleSelectMember = (memberId: string, checked: boolean) => {
    const updatedMembers = members.map((member) => {
      if (member.memberId === memberId) {
        // Allow allocation for any non-zero outstanding (positive or negative)
        const allocateAmount = checked && member.outstanding !== 0 ? Math.abs(member.outstanding) : 0;
        return {
          ...member,
          selected: checked && member.outstanding !== 0,
          amountToAllocate: allocateAmount,
        };
      }
      return member;
    });
    setMembers(updatedMembers);
    calculateAllocatedAmount(updatedMembers);
  };

  const handleSelectAll = (checked: boolean) => {
    const updatedMembers = members.map((member) => {
      // Only select members with non-zero outstanding balances
      const canSelect = checked && member.outstanding !== 0;
      return {
        ...member,
        selected: canSelect,
        amountToAllocate: canSelect ? Math.abs(member.outstanding) : 0,
      };
    });
    setMembers(updatedMembers);
    calculateAllocatedAmount(updatedMembers);
  };

  const handleAmountChange = (memberId: string, amount: number | null) => {
    const updatedMembers = members.map((member) => {
      if (member.memberId === memberId) {
        // Ensure amount is never negative
        const sanitizedAmount = Math.max(0, amount || 0);
        const updated = {
          ...member,
          amountToAllocate: sanitizedAmount,
          selected: sanitizedAmount > 0,
        };
        return updated;
      }
      return member;
    });
    setMembers(updatedMembers);
    calculateAllocatedAmount(updatedMembers);
  };

  const calculateAllocatedAmount = (membersList: IMemberPayment[]) => {
    const total = membersList.reduce(
      (sum, member) => sum + member.amountToAllocate,
      0
    );
    setAllocatedAmount(total);
  };

  const handleAutoDistribute = () => {
    if (totalAmount <= 0) {
      message.warning("Please enter a total payment amount first");
      return;
    }

    const membersWithOutstanding = members.filter((m) => m.outstanding > 0);
    
    if (membersWithOutstanding.length === 0) {
      message.info("No members have outstanding balances to allocate payments to.");
      return;
    }

    let remainingAmount = totalAmount;
    const updatedMembers = members.map((member) => {
      if (member.outstanding > 0 && remainingAmount > 0) {
        const amountToAllocate = Math.min(member.outstanding, remainingAmount);
        remainingAmount -= amountToAllocate;
        return {
          ...member,
          amountToAllocate,
          selected: amountToAllocate > 0,
        };
      }
      return {
        ...member,
        amountToAllocate: 0,
        selected: false,
      };
    });

    setMembers(updatedMembers);
    calculateAllocatedAmount(updatedMembers);

    if (remainingAmount > 0) {
      message.info(`R ${remainingAmount.toFixed(2)} remaining after allocation`);
    } else {
      message.success("Amount fully allocated to members");
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();

      const selectedMembers = members.filter(
        (m) => m.selected && m.amountToAllocate > 0
      );

      if (selectedMembers.length === 0) {
        message.warning("Please select at least one member and allocate payment");
        return;
      }

      if (totalAmount <= 0) {
        message.warning("Please enter a valid total payment amount");
        return;
      }

      if (allocatedAmount > totalAmount) {
        message.error(
          `Allocated amount (R ${allocatedAmount.toFixed(2)}) exceeds total payment (R ${totalAmount.toFixed(2)})`
        );
        return;
      }

      setLoading(true);

      const paymentData = {
        groupId: group.id,
        totalAmount,
        paymentDate: paymentDate.format("YYYY-MM-DD"),
        paymentReference,
        paymentMethod,
        notes,
        allocations: selectedMembers.map((member) => ({
          memberId: member.memberId,
          amount: member.amountToAllocate,
        })),
      };

      const response = await addBulkPaymentApi(paymentData);

      // Check if response was successful (could be response.success or response.data.success)
      const isSuccess = response?.success || response?.data?.success;
      
      if (isSuccess) {
        message.success(
          `Successfully allocated payments to ${selectedMembers.length} member(s)`
        );
        handleModal();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMessage = response?.message || response?.data?.message || "Failed to process bulk payment";
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error processing bulk payment:", error);
      
      if (error.errorFields) {
        message.error("Please fill in all required fields");
      } else {
        message.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to process bulk payment"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: (
        <Checkbox
          onChange={(e) => handleSelectAll(e.target.checked)}
          checked={members.length > 0 && members.every((m) => m.selected)}
          indeterminate={
            members.some((m) => m.selected) && !members.every((m) => m.selected)
          }
        >
          Select
        </Checkbox>
      ),
      key: "select",
      width: 80,
      render: (_: any, record: IMemberPayment) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleSelectMember(record.memberId, e.target.checked)}
          disabled={record.outstanding === 0}
        />
      ),
    },
    {
      title: "Member",
      key: "member",
      render: (_: any, record: IMemberPayment) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.memberName}</div>
          <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
            {record.membershipNumber}
          </div>
        </div>
      ),
    },
    {
      title: "Premium",
      dataIndex: "premium",
      key: "premium",
      width: 120,
      render: (amount: number) => `R ${amount.toFixed(2)}`,
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding",
      key: "outstanding",
      width: 120,
      render: (amount: number) => {
        // Both positive and negative non-zero amounts are debt (red)
        // Zero is paid up (green)
        const color = amount === 0 ? "green" : "red";
        return <Tag color={color}>R {amount.toFixed(2)}</Tag>;
      },
    },
    {
      title: "Amount to Allocate",
      key: "amountToAllocate",
      width: 180,
      render: (_: any, record: IMemberPayment) => (
        <InputNumber
          min={0}
          max={Math.abs(record.outstanding)}
          value={record.amountToAllocate}
          onChange={(value) => handleAmountChange(record.memberId, value)}
          style={{ width: "100%" }}
          prefix="R"
          precision={2}
          disabled={record.outstanding === 0}
        />
      ),
    },
  ];

  const selectedCount = members.filter((m) => m.selected).length;
  const totalOutstanding = members.reduce((sum, m) => sum + m.outstanding, 0);
  const remainingAmount = totalAmount - allocatedAmount;

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined />
          <span>Bulk Payment Allocation - {group.GroupName}</span>
        </Space>
      }
      open={show}
      onCancel={handleModal}
      width={1200}
      footer={null}
    >
      <Form form={form} layout="vertical">
        <Card
          title="Payment Details"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Total Payment Amount"
                name="totalAmount"
                rules={[{ required: true, message: "Please enter payment amount" }]}
              >
                <InputNumber
                  min={0}
                  value={totalAmount}
                  onChange={(value) => setTotalAmount(value || 0)}
                  style={{ width: "100%" }}
                  prefix="R"
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Payment Date"
                name="paymentDate"
                rules={[{ required: true, message: "Please select payment date" }]}
                initialValue={paymentDate}
              >
                <DatePicker
                  value={paymentDate}
                  onChange={(date) => setPaymentDate(date || dayjs())}
                  style={{ width: "100%" }}
                  format="DD MMM YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Payment Method"
                name="paymentMethod"
                rules={[{ required: true, message: "Please select payment method" }]}
                initialValue={paymentMethod}
              >
                <Select
                  value={paymentMethod}
                  onChange={setPaymentMethod}
                  style={{ width: "100%" }}
                >
                  <Option value="Cash">Cash</Option>
                  {/* <Option value="Bank Transfer">Bank Transfer</Option>
                  <Option value="EFT">EFT</Option>
                  <Option value="Cheque">Cheque</Option>
                  <Option value="Debit Order">Debit Order</Option>
                  <Option value="Other">Other</Option> */}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Payment Reference" name="paymentReference">
                <Input
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Enter payment reference number"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Notes" name="notes">
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this payment"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Payment Summary"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={6}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Total Payment
                </div>
                <div style={{ fontSize: "20px", fontWeight: "bold", color: "#1890ff" }}>
                  R {totalAmount.toFixed(2)}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Allocated
                </div>
                <div style={{ fontSize: "20px", fontWeight: "bold", color: "#52c41a" }}>
                  R {allocatedAmount.toFixed(2)}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Remaining
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: remainingAmount < 0 ? "#ff4d4f" : "#faad14",
                  }}
                >
                  R {remainingAmount.toFixed(2)}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Members Selected
                </div>
                <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                  {selectedCount} / {members.length}
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        <Card
          title={
            <Space>
              <span>Member Allocations</span>
              <Tooltip title="Total outstanding across all members">
                <Tag color="orange">
                  Total Outstanding: R {totalOutstanding.toFixed(2)}
                </Tag>
              </Tooltip>
            </Space>
          }
          size="small"
          extra={
            <Button
              type="primary"
              size="small"
              onClick={handleAutoDistribute}
              disabled={totalAmount <= 0}
            >
              Auto Distribute
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={members}
            rowKey="memberId"
            loading={loadingMembers}
            pagination={false}
            scroll={{ y: 400 }}
            size="small"
          />
        </Card>

        <Divider />

        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={handleModal}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={selectedCount === 0 || totalAmount <= 0}
              icon={<CheckCircleOutlined />}
            >
              Process Payment Allocation
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default BulkPaymentModal;
